import useBreakPoints from '@components/Banner/useBreakPoints';
import {
  LoadingNextAvailable,
  NextAvailablePill,
} from '@components/NextAvailable';
import SearchBar from '@components/SearchBar';
import { Icon } from '@drdropin-tech/theseus';
import useAnalytics from '@hooks/useAnalytics';
import {
  fetchClinics,
  fetchNextAvailableAcrossClinics,
  fetchPractitioners,
  LegacyClinic,
} from '@packages/cortex-sdk';
import {
  localesNextAvailableWithTime,
  timeZone,
  useIntersectionObserver,
  useNavigationStore,
  useServiceTreeStore,
} from '@packages/shared';
import { Actions, Locales, Practitioner } from '@packages/types';
import {
  AnimatedStep,
  SpinnerWrapper,
  StepButton,
  StepWrapper,
} from '@packages/ui-web';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { formatRelative, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Drawer } from 'vaul';
import { shallow } from 'zustand/shallow';

type PractitionerClinic = { id: string; name: string; city: string };

export default function SelectGPPractitionerStep() {
  const { t } = useTranslation(['booking', 'common']);
  const setEventProps = useAnalytics();
  const [practitionersFilterQuery, setPractitionersFilterQuery] = useState('');
  const [selectedClinics, setSelectedClinics] = useState<
    Map<string, PractitionerClinic>
  >(new Map());

  const { queries, setQueries, advanceStep } = useNavigationStore(
    (state) => ({
      queries: state.queries,
      setQueries: state.setQueries,
      advanceStep: state.advanceStep,
    }),
    shallow,
  );

  const { getServiceUuid } = useServiceTreeStore(
    (state) => ({
      getServiceUuid: state.getServiceUuid,
    }),
    shallow,
  );

  const serviceUuid = queries.service
    ? getServiceUuid(queries.service) ?? ''
    : '';

  const { data: practitioners, isLoading } = useQuery(
    ['practitioners', serviceUuid],
    ({ signal }) =>
      fetchPractitioners({
        serviceId: serviceUuid,
        signal,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!serviceUuid,
      keepPreviousData: true,
      select: (data) => {
        return data;
      },
    },
  );

  //new approach for clinics
  const { data: clinics } = useQuery(
    ['clinics', serviceUuid],
    ({ signal }) =>
      fetchClinics({
        serviceId: serviceUuid,
        signal,
      }),
    {
      enabled: !!serviceUuid,
      refetchOnWindowFocus: false,
    },
  );

  const clinicMap = new Map<string, LegacyClinic>(
    clinics?.map((c) => [c.id, c]),
  );
  const cities = new Set(clinics?.map((c) => c.address.city));

  const filteredPractitioners = practitioners?.filter(
    (p) =>
      // show results for practitioners that match:
      // search query
      p.name.toLowerCase().includes(practitionersFilterQuery.toLowerCase()) &&
      // and selected clinics
      (selectedClinics.size > 0
        ? p.clinics?.some((c) => selectedClinics.has(c.id))
        : true) &&
      // and practitioner must be in one of the clinics
      (clinics ? p.clinics?.some((c) => clinicMap.has(c.id)) : true),
  );

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPractitionersFilterQuery(value);
    // only capture the search query event if the patient pauses for 1 second
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      if (value.trim()) {
        setEventProps({
          event: {
            action: Actions.ChooseDoctorSearch,
            searchQuery: value.trim(),
          },
        });
      }
    }, 1000);
  };

  const handleClick = (practitioner?: Practitioner) => {
    if (practitioner) {
      setQueries({ practitionerId: practitioner.id });
      setEventProps({
        event: {
          action: Actions.ChooseDoctorSelectPractitioner,
          practitionerId: practitioner.id,
        },
      });
    }
    advanceStep();
  };

  return (
    <StepWrapper
      isLoading={isLoading}
      stepWidth="max-w-[512px]"
      title={t('steps.select_practitioner.title')}
    >
      {isLoading ? (
        <SpinnerWrapper />
      ) : (
        <>
          <div className="mb-4 flex w-full flex-col gap-2">
            <div className="flex w-full flex-wrap justify-start gap-2">
              <PractitionerFilterDrawer
                clinicMap={clinicMap}
                cities={cities}
                selectedClinics={selectedClinics}
                setSelectedClinics={setSelectedClinics}
              />
              {selectedClinics.size > 0 &&
                [...selectedClinics.values()].map((clinic) => (
                  <div
                    key={clinic.id}
                    className="bg-accent flex flex-row items-center gap-2 rounded-full px-4 text-black"
                  >
                    <span className="text-[17px]"> {clinic.name}</span>

                    <button
                      onClick={() =>
                        setSelectedClinics((prev) => {
                          const next = new Map(prev);
                          next.delete(clinic.id);
                          return next;
                        })
                      }
                    >
                      <Icon name="X" iconSize="sm" />
                    </button>
                  </div>
                ))}
            </div>
            <div className="flex w-full flex-col justify-start gap-2">
              <SearchBar
                placeholder={t(
                  'booking:steps.select_practitioner.search_for_practitioner',
                )}
                onChangeText={handleChange}
              />
            </div>
          </div>
          {filteredPractitioners?.map(
            (practitioner: Practitioner, index: number) => (
              <PractitionerCard
                key={practitioner.id}
                practitioner={practitioner}
                serviceId={serviceUuid}
                // adding a max index of 15 to prevent long delays when searching for practitioners
                index={Math.min(index, 15)}
                handleClick={handleClick}
                clinicIds={
                  selectedClinics.size
                    ? [...selectedClinics.values()].map((c) => c.id).sort()
                    : undefined
                }
              />
            ),
          )}
        </>
      )}
    </StepWrapper>
  );
}

function PractitionerFilterDrawer({
  clinicMap,
  cities,
  selectedClinics,
  setSelectedClinics,
}: {
  clinicMap: Map<string, LegacyClinic>;
  cities: Set<string>;
  selectedClinics: Map<string, PractitionerClinic>;
  setSelectedClinics: React.Dispatch<
    React.SetStateAction<Map<string, PractitionerClinic>>
  >;
}) {
  const setEventProps = useAnalytics();

  const { t } = useTranslation(['booking', 'common']);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [draftClinics, setDraftClinics] = useState<
    Map<string, PractitionerClinic>
  >(new Map(selectedClinics));

  const { isXl } = useBreakPoints('xl');
  const isSidebar = isXl;
  const snapPoints = isSidebar ? [1] : [0.5, 0.9];
  const [snap, setSnap] = useState<number | string | null>(
    snapPoints[snapPoints.length - 1],
  );

  useEffect(() => {
    setSnap(snapPoints[snapPoints.length - 1]);
  }, [isSidebar]);

  useEffect(() => {
    if (openDrawer) {
      setDraftClinics(new Map(selectedClinics));
    }
  }, [openDrawer]);

  // Group clinics by city
  const clinicsByCity = new Map<string, LegacyClinic[]>();
  for (const clinic of clinicMap.values()) {
    const city = clinic.address.city;
    if (!clinicsByCity.has(city)) {
      clinicsByCity.set(city, []);
    }
    clinicsByCity.get(city)!.push(clinic);
  }

  const handleSelectAllInCity = (city: string) => {
    const cityClinics = clinicsByCity.get(city) || [];
    const allSelected = cityClinics.every((c) => draftClinics.has(c.id));

    setEventProps({
      event: {
        action: Actions.ChooseDoctorFilterSelectAll,
        city,
      },
    });

    setDraftClinics((prev) => {
      const next = new Map(prev);
      if (allSelected) {
        cityClinics.forEach((c) => next.delete(c.id));
      } else {
        cityClinics.forEach((c) =>
          next.set(c.id, {
            id: c.id,
            name: c.name,
            city: c.address.city,
          }),
        );
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const draftClinicsList = [...draftClinics.values()];
    const clinicIds = draftClinicsList.map((c) => c.id);
    const selectedCities = [...new Set(draftClinicsList.map((c) => c.city))];

    setEventProps({
      event: {
        action: Actions.ChooseDoctorFilterCompleted,
        clinicIds,
        city: selectedCities.join(','),
      },
    });
    setSelectedClinics(new Map(draftClinics));
    setOpenDrawer(false);
  };
  const [clinicFilterQuery, setClinicFilterQuery] = useState('');

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setClinicFilterQuery(value);
  };

  return (
    <Drawer.Root
      open={openDrawer}
      onOpenChange={(open) => {
        if (open) {
          setSnap(snapPoints[snapPoints.length - 1]);
          setEventProps({
            event: {
              action: Actions.ChooseDoctorFilterOpened,
            },
          });
        }
        setOpenDrawer(open);
      }}
      snapPoints={snapPoints}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      modal={true}
      direction={isSidebar ? 'left' : 'bottom'}
    >
      <Drawer.Trigger className="hover:bg-accent/30 flex flex-row items-center gap-2 rounded-full border border-white px-4">
        <Icon name="Filter" iconSize="xs" className="text-white" />
        <p className="text-[17px] text-white">{t('common:filter')}</p>
      </Drawer.Trigger>
      <Drawer.Portal>
        <div className="pointer-events-none fixed inset-0 " />
        <Drawer.Content
          className={`fixed inset-x-0 bottom-0 flex h-full w-full flex-col bg-white outline-none ${
            isSidebar
              ? 'max-w-[360px] rounded-br-2xl rounded-tr-2xl'
              : 'mx-auto max-w-md rounded-t-2xl'
          }`}
        >
          <div className="relative flex flex-col gap-2 p-4">
            {!isSidebar && (
              <div className="bg-gray-dark mx-auto mb-4 h-1.5 w-12 rounded-full" />
            )}
            <Drawer.Title className="flex justify-center pt-2">
              {t('steps.select_practitioner.choose_location')}
              <button
                className="absolute right-3 top-3"
                onClick={() => setOpenDrawer(false)}
              >
                <Icon name="X" iconSize="md" />
              </button>
            </Drawer.Title>
          </div>

          {/* Scrollable content */}
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-36 pt-0">
            <SearchBar
              placeholder={t('common:search')}
              onChangeText={handleSearch}
            />
            <div className="flex flex-col">
              {cities && cities.size > 0 ? (
                [...cities]
                  .sort((a, b) => {
                    if (a.toLowerCase() === 'oslo') return -1;
                    if (b.toLowerCase() === 'oslo') return 1;
                    return a.localeCompare(b);
                  })
                  .map((city) => {
                    const cityClinics = (clinicsByCity.get(city) || []).filter(
                      (c) =>
                        c.name
                          .toLowerCase()
                          .includes(clinicFilterQuery.toLowerCase()),
                    );
                    if (cityClinics.length === 0) return null;

                    const allSelected = cityClinics.every((c) =>
                      draftClinics.has(c.id),
                    );

                    return (
                      <div key={city} className="mb-4">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-[17px] font-bold text-black">
                            {city}
                          </span>
                          <button
                            onClick={() => handleSelectAllInCity(city)}
                            className="hover:bg-accent rounded-full px-2 text-[17px] text-gray-600 hover:text-black"
                          >
                            {allSelected
                              ? t('common:deselect_all')
                              : t('common:select_all')}
                          </button>
                        </div>
                        <div className="flex flex-col gap-1">
                          {cityClinics
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((clinic) => {
                              const isSelected = draftClinics.has(clinic.id);
                              return (
                                <LocationItem
                                  id={clinic.id}
                                  key={clinic.id}
                                  label={clinic.name}
                                  isSelected={isSelected}
                                  onToggle={(id) =>
                                    setDraftClinics((prev) => {
                                      const next = new Map(prev);
                                      next.has(id)
                                        ? next.delete(id)
                                        : next.set(id, {
                                            id,
                                            name: clinic.name,
                                            city: clinic.address.city,
                                          });
                                      return next;
                                    })
                                  }
                                />
                              );
                            })}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-sm">
                  {t('booking:steps.select_practitioner.no_clinics_found')}
                </p>
              )}
            </div>
          </div>
          <div className="relative mx-4">
            <button
              onClick={handleConfirm}
              className={classNames(
                'bg-primary absolute w-full rounded-full p-2 text-white hover:brightness-150',
                isSidebar ? 'bottom-5 ' : 'bottom-24',
              )}
            >
              {t('common:select')}
            </button>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function LocationItem({
  id,
  label,
  isSelected,
  onToggle,
}: {
  id: string;
  label: string;
  isSelected: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onToggle(id)}
      className={`hover:bg-accent  flex w-full items-center gap-2 rounded-md  p-3 text-left ${
        isSelected ? 'bg-accent hover:bg-accent/50' : ''
      }`}
    >
      {isSelected ? (
        <Icon name="CheckSquare" iconSize="md" className="shrink-0" />
      ) : (
        <Icon name="Square" iconSize="md" className="shrink-0" />
      )}
      <span className="text-left text-[17px]">{label}</span>
    </button>
  );
}

function PractitionerCard({
  practitioner,
  serviceId,
  index,
  clinicIds,
  handleClick,
}: {
  practitioner: Practitioner;
  serviceId: string;
  index: number;
  clinicIds?: string[];
  handleClick: (practitioner: Practitioner) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['booking', 'common']);

  const entry = useIntersectionObserver(ref, {
    freezeOnceVisible: true,
    rootMargin: '200px',
  });
  const isVisible = !!entry?.isIntersecting;
  const [shouldFetch, setShouldFetch] = useState(false);
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => setShouldFetch(true), 300);
    return () => clearTimeout(timer);
  }, [isVisible]);
  const locale = useRouter().locale as Locales;

  const { data: nextAvailable, isLoading } = useQuery(
    ['next-available-across', practitioner.id, serviceId, clinicIds],
    async ({ signal }) => {
      return await fetchNextAvailableAcrossClinics({
        serviceId,
        practitionerId: practitioner.id,
        signal,
        clinicIds,
      });
    },
    {
      enabled: shouldFetch && isVisible && !!serviceId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: (nextAvailableSlot) => {
        if (nextAvailableSlot) {
          const zonedDate = utcToZonedTime(
            parseISO(nextAvailableSlot.startTime),
            timeZone,
          );
          return {
            label: formatRelative(zonedDate, new Date(), {
              locale: localesNextAvailableWithTime[locale],
            }),
            time: zonedDate.getTime(),
          };
        }
      },
    },
  );
  const practitionerUnavailable =
    shouldFetch && isVisible && !nextAvailable && !isLoading;

  return (
    <div ref={ref} className="w-full">
      <AnimatedStep order={index}>
        <StepButton
          title={
            <div className="flex h-full w-full flex-row items-center">
              <p className="break-all text-sm text-black max-sm:text-xs">
                {practitioner.name}
              </p>
            </div>
          }
          onClick={() => handleClick(practitioner)}
          imageSrc={practitioner.photoUrl || '/illustrations/square-doctor.png'}
          className="group"
          minimize={true}
          TopRightDecorator={
            shouldFetch && isVisible ? (
              practitionerUnavailable ? (
                <div className="rounded-full bg-white/80 px-3 text-[14px]">
                  {t('steps.select_datetime.tooltip.date.no_available')}
                </div>
              ) : isLoading ? (
                <LoadingNextAvailable index={index} minimize={true} />
              ) : (
                <NextAvailablePill
                  label={nextAvailable?.label}
                  minimize={true}
                />
              )
            ) : null
          }
          disabled={practitionerUnavailable}
        />
      </AnimatedStep>
    </div>
  );
}
